// src/lib/remnantLabel.js
// 🏷️ Etiqueta identificadora del RETAL (PLAN-INVENTARIO-RETALES §5c).
// Cuerpo 4×6" sobre el MOTOR COMPARTIDO `label.js` (logo, QR, layout): código,
// material, medidas, DIRECCIÓN DE FLAUTA (flecha → M1, regla §5b) y ubicación.
import { newLabelDoc, drawQr, fmtDim, pdfText } from "./label";

/**
 * Construye (sin guardar) el PDF de la etiqueta de un retal. Devuelve el jsPDF doc.
 * @param {object} args
 * @param {object} args.remnant        doc de `remnants` (code, m1, m2, fluteDirection, …)
 * @param {string} [args.locationText] ubicación YA RESUELTA (paleta → zona actual, §4b)
 */
export async function renderRemnantLabel({ remnant, locationText }) {
  const r = remnant || {};
  const { doc, W, M, center } = await newLabelDoc();

  // Remnant code (large) + REMNANT tag
  doc.setFont("helvetica", "normal"); doc.setFontSize(10);
  doc.text("REMNANT", center, M + 64, { align: "center" });
  doc.setFont("helvetica", "bold"); doc.setFontSize(30);
  doc.text(String(r.code || ""), center, M + 92, { align: "center" });

  // Material + category
  const matName = r.materialSnapshot?.name || r.category || "—";
  doc.setFont("helvetica", "bold"); doc.setFontSize(12);
  doc.text(doc.splitTextToSize(String(matName).toUpperCase(), W - 2 * M), center, M + 114, { align: "center" });
  const sub = [
    r.category,
    r.materialSnapshot?.color,
    r.thickness?.value ? `${r.thickness.value} ${r.thickness.unit || "mm"}` : null,
  ].filter(Boolean).join("  ·  ");
  doc.setFont("helvetica", "normal"); doc.setFontSize(10);
  doc.text(doc.splitTextToSize(sub, W - 2 * M), center, M + 130, { align: "center" });

  // Measures (M1 × M2 in) — big, the floor reads this first.
  doc.setFont("helvetica", "bold"); doc.setFontSize(20);
  doc.text(`${fmtDim(r.m1)} x ${fmtDim(r.m2)} in`, center, M + 158, { align: "center" });

  // Flute direction (corrugated only): arrow + text, unambiguous on the floor (§5b).
  let y = M + 176;
  if (r.fluteDirection === "m1") {
    doc.setFont("helvetica", "bold"); doc.setFontSize(12);
    doc.text("FLUTE → M1", center, y, { align: "center" });
    doc.setLineWidth(1.5);
    doc.line(center - 50, y + 8, center + 50, y + 8);
    doc.line(center + 50, y + 8, center + 42, y + 4);
    doc.line(center + 50, y + 8, center + 42, y + 12);
    y += 22;
  }

  // QR (scan to resolve the remnant at the scanning station).
  const payload = r.qrPayload || JSON.stringify({ t: "remnant", code: r.code });
  const qrSize = 120;
  if (await drawQr(doc, payload, center - qrSize / 2, y, qrSize)) y += qrSize + 20; else y += 16;

  // Footer: qty + location (pallet code or fixed rack/zone) + date.
  doc.setDrawColor(200); doc.line(M, y - 8, W - M, y - 8);
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold"); doc.text("Pieces:", M, y + 6);
  doc.setFont("helvetica", "normal"); doc.text(String(r.quantity ?? 1), M + 55, y + 6);
  y += 18;
  doc.setFont("helvetica", "bold"); doc.text("Location:", M, y + 6);
  doc.setFont("helvetica", "normal");
  doc.text(doc.splitTextToSize(pdfText(locationText || r.location?.label || "—"), W - M - 80), M + 60, y + 6);
  y += 18;
  const fecha = (() => { try { return r.createdAt?.toDate ? r.createdAt.toDate().toLocaleDateString() : new Date().toLocaleDateString(); } catch (_) { return ""; } })();
  doc.setFont("helvetica", "bold"); doc.text("Date:", M, y + 6);
  doc.setFont("helvetica", "normal"); doc.text(String(fecha), M + 42, y + 6);

  return doc;
}

/** Genera y DESCARGA la etiqueta del retal (on-demand desde alta y lista). */
export async function buildRemnantLabelPdf(args) {
  const doc = await renderRemnantLabel(args);
  doc.save(`LABEL_${args.remnant?.code || "REMNANT"}.pdf`);
}
